import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  User,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  sendEmailVerification,
} from 'firebase/auth';
import { auth } from '../firebase/config';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signup: (email: string, password: string, firstName: string, lastName: string) => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  getIdToken: () => Promise<string | null>;
  resendVerificationEmail: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const signup = async (email: string, password: string, firstName: string, lastName: string) => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const fullName = `${firstName} ${lastName}`.trim();
    try {
      await updateProfile(userCredential.user, { displayName: fullName });
    } catch (e) {
      // Non-fatal; continue even if displayName update fails
      // eslint-disable-next-line no-console
      console.warn('Failed to update display name:', e);
    }
    await sendEmailVerification(userCredential.user);
    setUser(userCredential.user);
  };

  const login = async (email: string, password: string) => {
    const credential = await signInWithEmailAndPassword(auth, email, password);
    setUser(credential.user);
  };

  const logout = async () => {
    await signOut(auth);
  };

  const resendVerificationEmail = async () => {
    const current = auth.currentUser;
    if (!current) {
      throw new Error('Sign in to resend the verification email.');
    }
    await sendEmailVerification(current);
  };

  const refreshUser = async () => {
    const current = auth.currentUser;
    if (!current) {
      return;
    }
    await current.reload();
    setUser(auth.currentUser);
  };

  const getIdToken = async (): Promise<string | null> => {
    const current = auth.currentUser;
    if (current) {
      return await current.getIdToken(true);
    }
    return null;
  };

  const value = {
    user,
    loading,
    signup,
    login,
    logout,
    getIdToken,
    resendVerificationEmail,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};



